import * as joint from '@joint/core';
import { DrawnLine } from './drawable-elements';
import { getLinkEndId } from './graph-load';
import { isIconLabel } from './icons';

// Fan-out grouping can leave several flows with the same visual endpoints, type, labels, and
// connector style. Drawing all of them stacks identical arrows; hiding all but one is unsafe
// because deleting the visible arrow would leave the others behind.
//
// So coincident flows collapse to **one arrow, which is one cell**. The flows it stands for are
// metadata on that cell — a `mergedFlows` array — not cells of their own. Nothing outside this
// module ever sees a flow that has no cell: the graph, the editor canvas, and the inspection
// snapshot all agree that there is exactly one arrow here, carrying a record of the flows it
// represents.
//
// A folded record stores only what distinguishes its flow. Shared display attributes come from the
// survivor; per-flow routing is preserved separately so splitting can restore it.
//
// Expansion is transient graph normalization, not a view of the diagram. Callers expand only while
// an operation needs to address constituent flows individually, then collapse before serializing or
// exposing the live graph again. A cell id still refers to the drawn arrow, so operations on it
// apply to the whole group (see linkGroupFor).

export interface FoldedFlow {
    cellId: string;
    sourceEntityId: string;
    targetEntityId: string;
    // Three-state, exactly as persisted: absent, or present — including the empty string. Never
    // normalise the two together (see readStepId).
    stepId?: string;
    agentAdded?: boolean;
    // Preserve-only: a folded flow's own hand-routed vertices, restored on expansion. Never taken
    // from, or applied to, the survivor — the merged arrow always renders the survivor's route.
    vertices?: { x: number; y: number }[];
}

// Written onto each restored constituent for one normalization operation, naming the drawn arrow it
// came out of, then stripped on collapse. This lets a caller-supplied arrow id reach every flow that
// arrow represents. Never persisted.
const EXPANDED_FROM = 'expandedFromCellId';

// Restores each merged flow to its own link for the duration of a graph operation. Idempotent, and
// a no-op on a graph with no merged arrows (e.g. every existing golden fixture).
export function expandMergedFlows(graph: joint.dia.Graph): void {
    // Snapshot first: newly added cells never carry mergedFlows, so iterating a live view would be
    // equivalent, but a fixed array is the less surprising contract to maintain.
    const links = [...graph.getLinks()];
    for (const link of links) {
        const folded = getMergedFlows(link);
        if (folded.length === 0) {
            continue;
        }
        for (const flow of folded) {
            graph.addCell(buildMergedFlowClone(link, flow));
        }
        link.removeProp('mergedFlows');
    }
}

// Collapses coincident flows back onto one arrow after constituent-level work finishes. Batch
// callers do this after visibility normalization and before layout; live-editor callers do it
// before notifying code that may inspect or save the graph.
export function collapseCoincidentFlows(graph: joint.dia.Graph): void {
    for (const members of computeMergeGroups(graph)) {
        const [survivor, ...rest] = members;
        const folded = rest.map(captureMergedFlow).sort((a, b) => a.cellId.localeCompare(b.cellId));
        graph.removeCells(rest);
        survivor.prop('mergedFlows', folded, { rewrite: true });
    }
    // Normalization is over: drop the transient marker from links that ended up standalone too.
    for (const link of graph.getLinks()) {
        if (link.prop(EXPANDED_FROM) !== undefined) {
            link.removeProp(EXPANDED_FROM);
        }
    }
}

// The flows this arrow represents besides its own; [] for an ordinary arrow. The arrow's own
// source/target/step attribution is on the cell itself, so the flows it stands for are this list
// plus that.
export function getMergedFlows(link: joint.dia.Link): FoldedFlow[] {
    const raw = link.prop('mergedFlows');
    return Array.isArray(raw) ? raw as FoldedFlow[] : [];
}

// Distinct step-owner values represented by an arrow. `undefined` is retained as a real state for
// visual-only flows; malformed and empty ids are retained so inspection can classify them as
// orphaned. Ordering follows the survivor, then its folded flows.
function representedStepIds(link: joint.dia.Link): Array<string | undefined> {
    const result: Array<string | undefined> = [];
    const seen = new Set<string>();
    for (const stepId of [readStepId(link), ...getMergedFlows(link).map(flow => flow.stepId)]) {
        const key = stepId === undefined ? 'absent' : `present:${stepId}`;
        if (!seen.has(key)) {
            seen.add(key);
            result.push(stepId);
        }
    }
    return result;
}

// Real or orphaned owner ids represented by an arrow, de-duplicated. Visual-only constituents have
// no owner id and are intentionally omitted.
export function ownedStepIds(link: joint.dia.Link): string[] {
    return representedStepIds(link).filter((stepId): stepId is string => stepId !== undefined);
}

// Every link that the drawn arrow `cellId` stands for while expanded: the cell itself plus any
// constituent expandMergedFlows restored from it. Callers addressing a cell id from an inspection
// snapshot mean the arrow, so an edit or removal has to reach all of them — otherwise patching the
// arrow would desynchronise its flows (and fan it back out at the next collapse), and removing it
// would leave the rest behind to re-collapse into a replacement.
export function linkGroupFor(graph: joint.dia.Graph, link: joint.dia.Link): joint.dia.Link[] {
    const cellId = String(link.id);
    const expanded = graph.getLinks().filter(candidate => candidate.prop(EXPANDED_FROM) === cellId);
    return [link, ...expanded];
}

// A link's step id is three-state: absent means visual-only, while present-but-unresolvable — an
// empty string or any malformed value — means orphaned. The engine carries every value through
// unchanged; C# inspection classifies it against the current step list.
function readStepId(link: joint.dia.Link): string | undefined {
    const raw = link.prop('stepId');
    return raw === null || raw === undefined ? undefined : String(raw);
}

function computeMergeGroups(graph: joint.dia.Graph): joint.dia.Link[][] {
    const buckets = new Map<string, joint.dia.Link[]>();
    for (const link of graph.getLinks()) {
        if (!isMergeCandidate(link)) {
            continue;
        }
        const sourceId = getLinkEndId(link, 'source');
        const targetId = getLinkEndId(link, 'target');
        // A self-loop (both endpoint entities sharing one node) is already handled — hidden, not
        // merged — by updateSameNodeLinkVisibility; folding it in here would just add churn.
        if (!sourceId || !targetId || sourceId === targetId) {
            continue;
        }
        const key = mergeSignature(link, sourceId, targetId);
        const bucket = buckets.get(key);
        if (bucket) {
            bucket.push(link);
        } else {
            buckets.set(key, [link]);
        }
    }

    const groups: joint.dia.Link[][] = [];
    for (const members of buckets.values()) {
        if (members.length < 2) {
            continue;
        }
        const sorted = [...members].sort((left, right) => String(left.id).localeCompare(String(right.id)));
        groups.push(sorted);
    }
    return groups;
}

// A flow can only fold into an arrow if it can be told apart from its siblings again later: an
// unattributed endpoint (a legacy link, or a group-level projection — see
// reconcileStepProjection's group coverage in graph-mutations.ts) has no member to restore it to,
// so it is excluded rather than merged. DrawnLine is not a flow.
function isMergeCandidate(link: joint.dia.Link): boolean {
    if (!(link instanceof joint.shapes.standard.Link) || link instanceof DrawnLine) {
        return false;
    }
    const sourceEntityId = link.prop('sourceEntityId');
    const targetEntityId = link.prop('targetEntityId');
    return typeof sourceEntityId === 'string' && sourceEntityId.length > 0
        && typeof targetEntityId === 'string' && targetEntityId.length > 0;
}

// Ownership is deliberately absent: step identity and provenance are not visual properties.
// JSON preserves field and label boundaries even when user-authored text contains control chars.
function mergeSignature(link: joint.dia.Link, sourceId: string, targetId: string): string {
    const type = String(link.get('type') ?? '');
    const connectorStyle = normalizedConnectorStyle(link);
    const labelSignature = computeLabelSignature(link);
    return JSON.stringify([sourceId, targetId, type, connectorStyle, labelSignature]);
}

// Matches applyConnectorStyleMixin's own fallback (constants.ts) without requiring the mixin to
// have run: a freshly loaded, not-yet-mutated link may not carry it yet.
function normalizedConnectorStyle(link: joint.dia.Link): string {
    const raw = link.prop('connectorStyle');
    return typeof raw === 'string' && raw.length > 0 ? raw : 'sharp';
}

// Mirrors graph-inspection.ts's readLinkLabels filtering (an empty text label is hidden, not
// removed) as a small local copy rather than an import, so this module stays independent of the
// inspector and usable from the live editor on its own.
function computeLabelSignature(link: joint.dia.Link): string[] {
    const labels = link.labels() ?? [];
    const parts: string[] = [];
    for (const label of labels) {
        const attrs = (label as { attrs?: { rect?: { visibility?: string }; text?: { text?: string } } }).attrs;
        if (attrs?.rect?.visibility === 'hidden') {
            continue;
        }
        if (isIconLabel(label)) {
            parts.push(`icon:${String((label as { iconName?: unknown }).iconName ?? '')}`);
            continue;
        }
        parts.push(`text:${attrs?.text?.text ?? ''}`);
    }
    return parts;
}

function captureMergedFlow(link: joint.dia.Link): FoldedFlow {
    const folded: FoldedFlow = {
        cellId: String(link.id),
        sourceEntityId: String(link.prop('sourceEntityId')),
        targetEntityId: String(link.prop('targetEntityId')),
    };
    // Presence, not truthiness: an empty id is a real (orphaned) value that must survive the fold.
    const stepId = readStepId(link);
    if (stepId !== undefined) {
        folded.stepId = stepId;
    }
    if (link.prop('agentAdded') === true) {
        folded.agentAdded = true;
    }
    const vertices = link.vertices();
    if (vertices.length > 0) {
        folded.vertices = vertices.map(vertex => ({ x: vertex.x, y: vertex.y }));
    }
    return folded;
}

// Clones the arrow's serialised JSON (attrs, labels, connectorStyle, type — everything the group
// matched on) and overrides only what distinguishes this flow. `new` on the arrow's own
// constructor reconstructs the correct concrete link subclass (PaymentLink, ServiceLink, ...)
// without a type-name lookup table, the same way graph.fromJSON resolves a cell's class from its
// registered namespace.
function buildMergedFlowClone(survivor: joint.dia.Link, flow: FoldedFlow): joint.dia.Link {
    const json = survivor.toJSON() as Record<string, unknown>;
    delete json.mergedFlows;
    json.id = flow.cellId;
    json.sourceEntityId = flow.sourceEntityId;
    json.targetEntityId = flow.targetEntityId;
    json[EXPANDED_FROM] = String(survivor.id);
    // Presence, not truthiness (see captureMergedFlow): restoring an empty id as absent would turn
    // an orphaned flow into a visual-only one the first time its group is split.
    if (flow.stepId !== undefined) {
        json.stepId = flow.stepId;
    } else {
        delete json.stepId;
    }
    if (flow.agentAdded) {
        json.agentAdded = true;
    } else {
        delete json.agentAdded;
    }
    // Preserve-only (see FoldedFlow.vertices): never inherited from the arrow's own route.
    json.vertices = flow.vertices ? flow.vertices.map(point => ({ ...point })) : [];

    const LinkConstructor = survivor.constructor as new (attributes: Record<string, unknown>) => joint.dia.Link;
    return new LinkConstructor(json);
}
